/**
 * Mitigates race conditions.
 * Simple wrapper/queue for adiTrack.
 * Removes the need for the promise.
 * Waits for GTM to load and sends out queued requests.
 */
class Track {
  constructor(options = {}) {
    // storage for events fired before GTM has loaded.
    this._queue = [];
    // once set to true by `this.checkLoaded`
    this.adiTrackLoaded = false;
    // how long we should wait for GTM to load
    this.loadTimeout = options.loadTimeout || 3000;
    // track each pass at `this.checkLoaded`
    this.loadTimeoutElapsed = options.loadTimeoutElapsed || 0;
    // how often `this.checkLoaded` should run
    this.loadTimeoutElapsedIncrement =
      options.loadTimeoutElapsedIncrement || 10;
    // start checking!
    this.checkLoaded();
  }

  /**
   * wait for adiTrack to load via GTM then process the queue.
   * @todo add a timeout: https://jira.advance.net/browse/ADITRACK-3
   * @return {void}
   */
  checkLoaded() {
    let timeout = setTimeout(() => {
      // fail first if the GTM load timeout has been reached
      if (this.loadTimeoutElapsed === this.loadTimeout) {
        this.page = noop;
        this.event = noop;
        clearInterval(timeout);
        return console.log("ADITrack could not be loaded.");
      }

      // grab the GTM injected script tag
      const tag = document.getElementById("adiTrackTag");
      // check the data-loaded attribute of the tag to see if it's loaded or not
      const tagAttrIsSet =
        tag && typeof tag !== "undefined" && tag.dataset.loaded === "true";
      // if tag does not exist or data-attr is not set, re-run the function
      if (tagAttrIsSet) {
        this.adiTrackLoaded = true;
        return this.processQueue();
      }

      // otherwise increment `this.loadTimeoutElapsed` by 10ms and re-run
      this.loadTimeoutElapsed =
        this.loadTimeoutElapsed + this.loadTimeoutElapsedIncrement;
      return this.checkLoaded();
    }, this.loadTimeoutElapsedIncrement);
  }

  /**
   * events
   * @param {object} props
   * @param {object} config
   */
  event(props, config) {
    return !this.adiTrackLoaded
      ? this._queue.push({ type: "event", args: [props, config] })
      : window._adiTrack.event(props, config);
  }

  /**
   * page view
   * @param {string} [url=window.location.href]
   */
  page(url = window.location.href) {
    return !this.adiTrackLoaded
      ? this._queue.push({ type: "page", args: [url] })
      : window._adiTrack.page(url);
  }

  /**
   * empty queued up requests
   * @return {void}
   */
  processQueue() {
    if (isDefined(this._queue) && Array.isArray(this._queue)) {
      this._queue.forEach(function(job, index) {
        if (
          window._adiTrack[job.type] &&
          isFunction(window._adiTrack[job.type])
        ) {
          window._adiTrack[job.type](...job.args);
        }
      });
    }
  }
}

const noop = function() {};

/**
 * @param {function} fn
 * @return {boolean}
 */
const isFunction = function isFunction(fn) {
  return fn && typeof fn === "function";
};

/**
 * [isDefined description]
 * @param {mixed} definition
 * @return {boolean}
 */
const isDefined = function isDefined(definition) {
  return definition && typeof definition !== "undefined";
};

/**
 * This is the default export of the module.
 * If adiTrack is already defined we use that, otherwise,
 * we namespace the module to `window.adiTrack` and once
 * GTM has loaded, it is namespaced to `window._adiTrack`
 * and all method calls are proxied to that namespace.
 * @type {object}
 */
let _default = null;

// if adiTrack already exists we'll just use that...
// otherwise, instantiate the track class and use that.
if (typeof window !== "undefined" && window.adiTrack) {
  _default = window.adiTrack;
} else if (typeof window !== "undefined") {
  _default = window.adiTrack = new Track();
}

export default _default;
